// apps/intranet/src/shell/LandingResolver.tsx
// ──────────────────────────────────────────────────────────────────────────────
// The element mounted at `/` (index route). Resolves where the user actually
// lands on each visit:
//
//   1. If the user has a saved `landingModule` preference AND can read that
//      module → redirect there. Skipped when the user clicked the sidebar's
//      Dashboard link (navigation state `fromNav`): that click must open the
//      dashboard. The state is cleared right away, so a reload lands normally.
//   2. Else if the user can read the default `dashboard` module → render it.
//   3. Else → redirect to the first accessible module in the registry.
//   4. Edge case (no modules accessible at all) → render <AccessDenied/>.
//
// Runs *after* permissions + preferences are loaded; shows a tiny placeholder
// while either is in flight so we never flicker the wrong page.
// ──────────────────────────────────────────────────────────────────────────────

import { useEffect, useMemo, useRef, Suspense } from 'react'
import { lazyWithReload as lazy } from './lazyWithReload'
import { useLocation, useNavigate } from 'react-router-dom'
import { modules, AccessDeniedPage } from '../modules/registry'
import { useEffectivePermissions } from '../auth/useEffectivePermissions'
import { useUserPreferences } from '../auth/useUserPreferences'

const DashboardPage = lazy(() => import('../pages/DashboardPage'))

function PlaceholderSpinner() {
  return (
    <div className="flex items-center justify-center h-64 text-on-surface-variant">
      <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
    </div>
  )
}

export default function LandingResolver() {
  const navigate = useNavigate()
  const location = useLocation()
  const fromNav = (location.state as { fromNav?: boolean } | null)?.fromNav === true
  // Remembered for as long as this element stays mounted: clearing the
  // navigation state below must not bring the saved-landing redirect back.
  const skipSaved = useRef(false)
  if (fromNav) skipSaved.current = true
  const perms = useEffectivePermissions()
  const prefs = useUserPreferences()

  const target = useMemo(() => {
    if (perms.isLoading || prefs.isLoading) return { state: 'loading' as const }

    const preferredId = prefs.get('landingModule')

    // 1) Saved preference, still accessible → redirect there.
    if (!skipSaved.current && preferredId && perms.canRead(preferredId)) {
      const m = modules.find(mod => mod.id === preferredId && !mod.noRoute)
      if (m) return { state: 'redirect' as const, path: m.path }
    }

    // 2) Default dashboard accessible → render in place.
    if (perms.canRead('dashboard')) return { state: 'dashboard' as const }

    // 3) Fall back to first accessible non-noRoute module.
    const first = modules.find(m => !m.noRoute && m.id !== 'settings' && perms.canRead(m.id))
    if (first) return { state: 'redirect' as const, path: first.path }

    // 4) No accessible modules — let the AccessDeniedPage render via <RoleGuard/>.
    return { state: 'denied' as const }
  }, [perms, prefs, fromNav])

  // Drop the one-shot navigation state so a reload keeps the normal landing behaviour.
  useEffect(() => {
    if (fromNav) navigate(location.pathname + location.search, { replace: true, state: null })
  }, [fromNav, navigate, location.pathname, location.search])

  useEffect(() => {
    if (target.state === 'redirect') navigate(target.path, { replace: true })
  }, [target, navigate])

  if (target.state === 'loading' || target.state === 'redirect') return <PlaceholderSpinner />
  if (target.state === 'denied') {
    // The user has NO accessible module (e.g. a role with only Bestellkorrektur
    // that hasn't been granted yet). Show a clear "Kein Zugriff" instead of
    // spinning forever — the previous placeholder never resolved → login hang.
    return (
      <Suspense fallback={<PlaceholderSpinner />}>
        <AccessDeniedPage />
      </Suspense>
    )
  }
  return (
    <Suspense fallback={<PlaceholderSpinner />}>
      <DashboardPage />
    </Suspense>
  )
}
